import { memo } from "react";
import SalesPartnerTile from "~/features/salesPartner/components/SalesPartnersTilesList/SalesPartnerTile";
import { type GoogleMarker, type PaginationDataType } from "~/domains/salesPartner/types/SalesPartnerTypes";
import Pagination from "~/features/search/components/common/Pagination";
import Spinner from "~/common/components/Spinner/Spinner";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/mui";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { selectSavedAffiliate, selectSavedSubsidiaryId } from "~/domains/affiliate/redux/selectors";
import { useSelector } from "react-redux";

interface Props {
  markers: GoogleMarker[];
  selectedMarker: GoogleMarker | undefined;
  onTileClick: (marker: GoogleMarker) => void;
  paginationData: PaginationDataType;
  markersLoading: boolean;
  updatePaginationCurrentPage: (newPageNumber: number) => void;
  onToggleDefault: (market: GoogleMarker, newValue: boolean) => void;
  showMoreMarkers: () => void;
  isCheckout: boolean;
}

const SalesPartnersTilesList = (props: Props) => {
  const { t } = useTranslation();

  const {
    markers,
    selectedMarker,
    onTileClick,
    paginationData,
    markersLoading,
    updatePaginationCurrentPage,
    showMoreMarkers,
    isCheckout,
  } = props;
  const pageAmount = paginationData.numberOfPages;

  const affiliateId = useSelector(selectSavedAffiliate)?.affiliateId;
  const subsidiaryId = useSelector(selectSavedSubsidiaryId);

  const hideShowMoreButton = paginationData.pageSize > paginationData.totalNumberOfResults;

  const isDesktop = useIsMinDesktopSize();

  return (
    <>
      {markersLoading && <Spinner />}
      {!markersLoading && (
        <>
          {markers.length === 0 && <div>{t("salesPartner.message.noResults")}</div>}
          {markers.length > 0 && (
            <>
              <ul className="scha__sp__list">
                {markers.map((marker) => (
                  <SalesPartnerTile
                    key={marker.unicId}
                    marker={marker}
                    isActive={selectedMarker ? marker.unicId === selectedMarker.unicId : false}
                    selectSalesPartner={onTileClick}
                    isDefault={!!subsidiaryId && marker.subsidiaryId === subsidiaryId}
                    isPreferred={!!affiliateId && marker.affiliateId === affiliateId}
                    onToggleDefault={(newValue) => props.onToggleDefault(marker, newValue)}
                  />
                ))}
              </ul>
              {(isDesktop || !isCheckout) && (
                <Pagination
                  pageAmount={pageAmount}
                  currentPage={paginationData.currentPage}
                  onPageChange={(pageNumber) => {
                    updatePaginationCurrentPage(pageNumber);
                  }}
                />
              )}
              {!hideShowMoreButton && !isDesktop && isCheckout && (
                <div className="w-100 d-flex flex-justify-center">
                  <Button onClick={showMoreMarkers}>{t("searchPage.refinements.showMore")}</Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default memo(SalesPartnersTilesList);
